import {
  ArrowRightLeft,
  BookOpen,
  BotMessageSquare,
  ChartColumn,
  Drama,
  Eye,
  FileUp,
  GraduationCap,
  type IconComponent,
  KeyRound,
  LayoutDashboard,
  ListTree,
  Mail,
  Megaphone,
  MessageSquareHeart,
  Presentation,
  Route,
  Search,
  Signpost,
  TextSearch,
  UserCheck,
  UserPlus,
  Users,
} from "@norican/icons";

/**
 * A destination in the header or the mobile drawer.
 *
 * `labelKey` is a key in the `common` namespace rather than a string, so the
 * desktop bar and the mobile drawer read the same translated label from one
 * place.
 */
export interface NavItem {
  /** Stable id: the React key. */
  id: NavItemId;
  /**
   * Route path, also the value matched against the current location. May carry
   * a query (`/access?view=completed`), which then has to match as well.
   */
  to: string;
  /** Key in the `common` namespace. */
  labelKey: `itAdminMenu.item.${Exclude<NavItemId, "starters">}` | "nav.starters";
  icon: IconComponent;
  /**
   * The section it lights up for, when that is wider than `to`: Access
   * requests open the list (`/access`) and stay highlighted on a case
   * (`/access/...`) and on the wizard.
   */
  activePrefix?: string;
  /** Carries the IT Admin count badge (access cases waiting for the reader). */
  badge?: "access";
}

export type NavItemId =
  | "starters"
  | "access"
  | "userLookup"
  | "viewAs"
  | "approvers"
  | "assignSuggest"
  | "routingRegister"
  | "emailIntake"
  | "ticketSearch"
  | "teamsBot"
  | "teams"
  | "messages"
  | "migrate"
  | "usage"
  | "feedback"
  | "leadershipReport"
  | "knowledgeOverview"
  | "knowledgeArticles"
  | "knowledgeBulkAdd"
  | "knowledgeAreas"
  | "training"
  | "demoPersonas";

export type NavGroupId = "people" | "desk" | "insights" | "knowledge" | "learning" | "management";

/** One heading in the "IT Admin ▾" menu and the mobile drawer. */
export interface NavGroup {
  id: NavGroupId;
  /** Key in the `common` namespace. */
  labelKey: `itAdminMenu.group.${NavGroupId}`;
  items: NavItem[];
}

/**
 * What `/auth/me` says about the reader, as far as the nav cares.
 *
 * Every flag is optional so the same file works on every branch: `dev` has
 * Teams (`can_view_teams`) and kiosk mode (`kiosk`) that test/STG lack, and
 * a flag a branch's API does not send is `undefined`, so its item is not shown.
 */
export interface NavMe {
  role?: string | null;
  can_migrate?: boolean | null;
  can_manage_teams?: boolean | null;
  /** May open the Teams area (8 Oct): all of IT (read-only), admins, team managers. */
  can_view_teams?: boolean | null;
  has_starter_cases?: boolean | null;
  access_front_door?: boolean | null;
  access_audit?: boolean | null;
  it_admin?: boolean | null;
  kiosk?: object | null;
  /** May start "View as" (an admin on VIEW_AS_EMAILS, not already viewing). */
  can_view_as?: boolean | null;
  /** May use the demo personas (19 Oct): the View-as admins, test systems only. */
  can_demo_as?: boolean | null;
  /** May read the Leadership report (8 Oct): on LEADERSHIP_REPORT_VIEWERS. */
  leadership_report?: boolean | null;
  /** May bulk add and manage areas (9 Oct): on KNOWLEDGE_EDITORS. */
  knowledge_editor?: boolean | null;
  /** May open Management → Approvers (12 Oct): admins and APPROVERS_EDITOR_TEAMS members. */
  approvers?: boolean | null;
  /** May use "Who should this go to?" and read the routing register (16 Oct). */
  assign_suggest?: boolean | null;
  /** May open IT Admin → Email intake (19 Oct): IT, while EMAIL_INTAKE_MODE is not off. */
  email_intake?: boolean | null;
  /** May open IT Admin → Ticket search (21 Oct): IT, while TICKET_SEARCH_ENABLED. */
  ticket_search?: boolean | null;
}

/**
 * Features the bundle itself may or may not contain, for items that have no
 * `/auth/me` flag of their own (Usage is `role === "admin"` only, and its page
 * exists on `dev` alone). `nav-features.ts` fills this in at build time; the
 * tests pass it explicitly.
 */
export interface NavFeatures {
  usage: boolean;
}

const NO_FEATURES: NavFeatures = { usage: false };

/**
 * The header's own links beside the brand, before "IT Admin ▾".
 *
 * Only "New starters" is left (2 Oct), for HR, managers and Backoffice who can
 * see a case (`has_starter_cases`), and only while the access front door is
 * off (5 Oct): with it on, New starter cases are listed under Access and in
 * My Tickets, and `/starters` lands there. "My tickets" is rendered by
 * `AppHeader` on the right, beside the language and the account menu.
 */
export function primaryNavItemsFor(me: NavMe | undefined): NavItem[] {
  if (me == null || me.kiosk != null) {
    return [];
  }
  if (me.has_starter_cases === true && me.access_front_door !== true) {
    return [{ id: "starters", to: "/starters", labelKey: "nav.starters", icon: UserPlus }];
  }
  return [];
}

/**
 * The "IT Admin ▾" menu (6 Oct, decision 35): every staff tool, grouped, each
 * shown only when the reader may use it today. The gates are the ones the
 * routes and the server already apply; this list controls visibility, not
 * access:
 *
 * - Access (requests and completed, one item): front door on and the IT Admin rule
 *   (`access_audit`; admins and IT since 6 Oct). Everyone else follows their
 *   cases from My Tickets.
 * - User lookup, IT training: `it_admin` (`<ItAdminLayout>` checks it too).
 * - Ticket search (21 Oct, Service desk): `ticket_search` (the IT Admin rule and
 *   TICKET_SEARCH_ENABLED; `<ItAdminLayout>` checks it too).
 * - Teams: `can_view_teams` (`<TeamsRoute>`; all of IT read-only, admins and
 *   team managers edit — 8 Oct).
 * - Management (9 Oct): Approvers (the approver matrix, `<ApproversRoute>`) for
 *   admins and, since 12 Oct, members of APPROVERS_EDITOR_TEAMS ("Backoffice
 *   Team") — `approvers`; room for more management tools.
 * - Manage messages, Feedback inbox, Usage: `role === "admin"` (`<AdminRoute>`).
 * - Migrate: `can_migrate` (`<MigratorRoute>`; migrators and admins).
 * - View as…: `can_view_as` (`<AdminRoute>`; admins on VIEW_AS_EMAILS; on LIVE only those set for prod).
 * - Demo personas (28 Oct): `can_demo_as` (never on LIVE); the test helpers' one page.
 * - Leadership report: `leadership_report` (`<LeadershipReportRoute>`; the IT
 *   Leadership Team and Rusty, LEADERSHIP_REPORT_VIEWERS — not every admin).
 * - Knowledge (9 Oct, its own group): Overview and Articles for `it_admin`
 *   (every IT user, read-only); Bulk add and Areas for `knowledge_editor`
 *   (KNOWLEDGE_EDITORS).
 *
 * Empty groups are dropped, and an empty result means no "IT Admin" at all.
 * Nothing on a shared device (kiosk): Migrate is refused there, a generic
 * shop-floor account is never an admin, and the staff areas are not for a
 * shared login.
 */
export function itAdminGroupsFor(
  me: NavMe | undefined,
  features: NavFeatures = NO_FEATURES,
): NavGroup[] {
  if (me == null || me.kiosk != null) {
    return [];
  }
  const admin = me.role === "admin";
  const access = me.access_front_door === true && me.access_audit === true;
  const itAdmin = me.it_admin === true;

  const people: NavItem[] = [];
  if (access) {
    // One "Access" item (7 Oct): the page has its own Requests / Completed tabs.
    people.push({
      id: "access",
      to: "/access",
      labelKey: "itAdminMenu.item.access",
      icon: KeyRound,
      activePrefix: "/access",
      badge: "access",
    });
  }
  if (itAdmin) {
    people.push({
      id: "userLookup",
      to: "/it-admin",
      labelKey: "itAdminMenu.item.userLookup",
      icon: Search,
    });
  }
  if (me.can_view_teams === true) {
    people.push({ id: "teams", to: "/teams", labelKey: "itAdminMenu.item.teams", icon: Users });
  }
  if (me.can_view_as === true) {
    people.push({
      id: "viewAs",
      to: "/admin/view-as",
      labelKey: "itAdminMenu.item.viewAs",
      icon: Eye,
    });
  }
  // 28 Oct (Rusty): the test helpers live on one admin page, test systems only.
  if (me.can_demo_as === true) {
    people.push({
      id: "demoPersonas",
      to: "/admin/demo-personas",
      labelKey: "itAdminMenu.item.demoPersonas",
      icon: Drama,
    });
  }

  const desk: NavItem[] = [];
  // 21 Oct: find any incident or request by any field (Helle, Systems Support).
  if (me.ticket_search === true) {
    desk.push({
      id: "ticketSearch",
      to: "/it-admin/ticket-search",
      labelKey: "itAdminMenu.item.ticketSearch",
      icon: TextSearch,
    });
  }
  if (admin) {
    desk.push({
      id: "messages",
      to: "/admin/messages",
      labelKey: "itAdminMenu.item.messages",
      icon: Megaphone,
    });
  }
  // 16 Oct: "Who should this go to?" for the dispatchers (all of IT by default).
  if (me.assign_suggest === true) {
    desk.push({
      id: "assignSuggest",
      to: "/it-admin/assign",
      labelKey: "itAdminMenu.item.assignSuggest",
      icon: Signpost,
    });
  }
  // 19 Oct: email intake (option B), the processed mails and the review queue.
  if (me.email_intake === true) {
    desk.push({
      id: "emailIntake",
      to: "/it-admin/email-intake",
      labelKey: "itAdminMenu.item.emailIntake",
      icon: Mail,
    });
  }
  if (me.can_migrate === true) {
    desk.push({
      id: "migrate",
      to: "/migrate",
      labelKey: "itAdminMenu.item.migrate",
      icon: ArrowRightLeft,
    });
  }

  const insights: NavItem[] = [];
  if (me.leadership_report === true) {
    insights.push({
      id: "leadershipReport",
      to: "/leadership-report",
      labelKey: "itAdminMenu.item.leadershipReport",
      icon: Presentation,
    });
  }
  if (admin && features.usage) {
    insights.push({
      id: "usage",
      to: "/admin/usage",
      labelKey: "itAdminMenu.item.usage",
      icon: ChartColumn,
    });
  }
  if (admin) {
    insights.push({
      id: "feedback",
      to: "/admin/feedback",
      labelKey: "itAdminMenu.item.feedback",
      icon: MessageSquareHeart,
    });
  }

  const knowledge: NavItem[] = [];
  if (itAdmin) {
    knowledge.push(
      {
        id: "knowledgeOverview",
        to: "/knowledge",
        labelKey: "itAdminMenu.item.knowledgeOverview",
        icon: LayoutDashboard,
      },
      {
        id: "knowledgeArticles",
        to: "/knowledge/articles",
        labelKey: "itAdminMenu.item.knowledgeArticles",
        icon: BookOpen,
        activePrefix: "/knowledge/articles",
      },
    );
    if (me.knowledge_editor === true) {
      knowledge.push(
        {
          id: "knowledgeBulkAdd",
          to: "/knowledge/bulk-add",
          labelKey: "itAdminMenu.item.knowledgeBulkAdd",
          icon: FileUp,
        },
        {
          id: "knowledgeAreas",
          to: "/knowledge/areas",
          labelKey: "itAdminMenu.item.knowledgeAreas",
          icon: ListTree,
        },
      );
    }
  }

  const learning: NavItem[] = [];
  if (itAdmin) {
    learning.push({
      id: "training",
      to: "/it-admin/training",
      labelKey: "itAdminMenu.item.training",
      icon: GraduationCap,
    });
  }
  // 28 Oct (Rusty): the access process training is unlinked (it teaches
  // retired steps); its pages stay at /learn/access and /admin/access-training.

  // Management (9 Oct, Rusty): tools that change how the HelpDesk works.
  // Approvers first (12 Oct: Backoffice too, `approvers`); more to come.
  const management: NavItem[] = [];
  // 16 Oct: the routing register; read by the suggester's audience, edited by IT management.
  if (me.assign_suggest === true) {
    management.push({
      id: "routingRegister",
      to: "/it-admin/routing",
      labelKey: "itAdminMenu.item.routingRegister",
      icon: Route,
    });
  }
  if (admin || me.approvers === true) {
    management.push({
      id: "approvers",
      to: "/admin/approvers",
      labelKey: "itAdminMenu.item.approvers",
      icon: UserCheck,
    });
  }
  // 19 Oct: the IT HelpDesk Teams bot's status, P1 channel and test card.
  if (admin) {
    management.push({
      id: "teamsBot",
      to: "/admin/teams-bot",
      labelKey: "itAdminMenu.item.teamsBot",
      icon: BotMessageSquare,
    });
  }

  const groups: NavGroup[] = [
    { id: "people", labelKey: "itAdminMenu.group.people", items: people },
    { id: "desk", labelKey: "itAdminMenu.group.desk", items: desk },
    { id: "insights", labelKey: "itAdminMenu.group.insights", items: insights },
    { id: "knowledge", labelKey: "itAdminMenu.group.knowledge", items: knowledge },
    { id: "learning", labelKey: "itAdminMenu.group.learning", items: learning },
    { id: "management", labelKey: "itAdminMenu.group.management", items: management },
  ];
  return groups.filter((group) => group.items.length > 0);
}

/** Every item in the groups, in menu order. */
export function flattenGroups(groups: readonly NavGroup[]): NavItem[] {
  return groups.flatMap((group) => group.items);
}

/** Whether the "IT Admin" count badge has anything to count: an item carries it. */
export function wantsAccessBadge(groups: readonly NavGroup[]): boolean {
  return flattenGroups(groups).some((item) => item.badge === "access");
}

function splitTo(to: string): { path: string; query: URLSearchParams | null } {
  const at = to.indexOf("?");
  return at === -1
    ? { path: to, query: null }
    : { path: to.slice(0, at), query: new URLSearchParams(to.slice(at + 1)) };
}

/**
 * Whether `pathname` (and `search`) sits inside `item`.
 *
 * Prefix matching keeps a section highlighted on its child routes, but the
 * boundary check stops `/tickets` also lighting up for `/ticketsomething`. An
 * item whose `to` carries a query matches only its exact path with that query.
 */
export function isNavItemActive(item: NavItem, pathname: string, search = ""): boolean {
  const { path, query } = splitTo(item.to);
  if (query !== null) {
    if (pathname !== path) {
      return false;
    }
    const current = new URLSearchParams(search);
    return [...query.entries()].every(([key, value]) => current.get(key) === value);
  }
  const section = item.activePrefix ?? path;
  return pathname === section || pathname.startsWith(`${section}/`);
}

/**
 * The one item among `items` the location belongs to, or null.
 *
 * Several can match (`/it-admin/training` is inside User lookup's
 * `/it-admin`), so the most
 * specific wins: a query match first, then the longest path.
 */
export function activeNavItem(
  items: readonly NavItem[],
  pathname: string,
  search = "",
): NavItem | null {
  let best: NavItem | null = null;
  let bestScore = -1;
  for (const item of items) {
    if (!isNavItemActive(item, pathname, search)) {
      continue;
    }
    const { path, query } = splitTo(item.to);
    const score = (query === null ? 0 : 10_000) + (item.activePrefix ?? path).length;
    if (score > bestScore) {
      best = item;
      bestScore = score;
    }
  }
  return best;
}
